import MapExploration from "../utils/MapExploration.js";
import MapNerdClassic from "../games/MapNerdClassic.js";
import MapNerdHard from "../games/MapNerdHard.js";
import StorageService from "../services/StorageService.js";
import { showConfirmation } from "../utils/ConfirmationPopup.js";

/**
 * Drives the Map Nerd scope selection: renders the "World + 6 continents" picker,
 * then instantiates the scoped game (Classic or Hard) for the chosen scope,
 * tears it down and restores a pristine game container when the player goes back.
 *
 * The SVG is `@@include`-d once and shared, and `finishGame()` removes the map /
 * country-to-guess nodes, so before each scope we replace `.game-container` with
 * a fresh clone — guaranteeing clean DOM and no stacked listeners.
 */
export default class MapNerdController {
    /** Zone scopes, in display order after "World". */
    static CONTINENTS = ["af", "eu", "as", "am"];

    /**
     * @param {"classic"|"hard"} mode
     */
    constructor(mode) {
        this.mode = mode;
        this.page = document.querySelector(`.page.map-nerd-${mode}`);
        if (!this.page) return;

        this.GameClass = mode === "hard" ? MapNerdHard : MapNerdClassic;
        // World gameId — also the key under which the help-popup preference lives,
        // so help isn't re-shown per continent.
        this.worldGameId = mode === "hard" ? "mapnerdhard" : "mapnerdclassic";

        this.gameContainer = this.page.querySelector(".game-container");
        this.progressContainer = this.page.querySelector(".progress-container");
        // Snapshot the pristine markup before any game mutates it — both the map
        // container (SVG/listeners removed on win) and the progress bar / hearts
        // (game-over hides them, infinite mode toggles their children).
        this.pristineContainer = this.gameContainer.cloneNode(true);
        this.pristineProgress = this.progressContainer.cloneNode(true);

        this.scopePicker = this.page.querySelector(".scope-picker");
        this.scopeGrid = this.page.querySelector(".scope-grid");

        this.game = null;
        this.helpShown = false;

        // Delegated on `this.page` (never cloned) rather than bound directly on
        // the buttons, since both now live inside `.progress-container`, which
        // is replaced by a fresh clone on every `startScope()`.
        this.page.addEventListener("click", (e) => {
            if (e.target.closest(".btn-restart-ingame")) {
                const lang = geoNerdApp.languageManager;
                showConfirmation(
                    lang.t("settings.confirmTitle"),
                    lang.t("settings.confirmMessage"),
                    () => this.restartScope()
                );
            }
            if (e.target.closest(".btn-back-to-scopes")) this.goBack();
        });

        this.showPicker();
    }

    /** Country codes forming a scope's denominator. */
    _scopeCodes(scope) {
        const pool = scope === "world"
            ? geoNerdApp.easyCountries
            : (geoNerdApp.easyCountriesByContinent[scope] || []);
        return pool.map(c => c.code);
    }

    showPicker() {
        this.renderPicker();
        this.gameContainer.style.display = "none";
        this.progressContainer.style.display = "none";
        if (this.scopePicker) this.scopePicker.style.display = "flex";
        // Drop the full-screen fixed layout so the page flows normally and the
        // footer below stays visible and clickable.
        this.page.classList.add("scope-selecting");
    }

    renderPicker() {
        if (!this.scopeGrid) return;
        const lang = geoNerdApp.languageManager;

        const tile = (scope, label) => {
            const { found, total, percent } = MapExploration.stats(this._scopeCodes(scope));
            // Only the World scope feeds the leaderboard / composite score (see
            // COMPOSITE_GAMES in server/src/routes/leaderboard.js) — continents are
            // training only, so the picker makes that explicit.
            const badgeClass = scope === "world" ? "scope-badge-ranked" : "scope-badge-training";
            const badgeLabel = lang.t(scope === "world" ? "mapExploration.rankedLabel" : "mapExploration.trainingLabel");
            return `
                <button type="button" class="scope-tile scope-${scope}" data-scope="${scope}">
                    <span class="scope-badge ${badgeClass}">${badgeLabel}</span>
                    <span class="scope-name">${label}</span>
                    <span class="scope-count">${found}/${total}</span>
                    <span class="scope-bar"><span class="scope-bar-fill" style="width: ${percent}%"></span></span>
                </button>
            `;
        };

        let html = tile("world", lang.t("mapExploration.worldLabel"));
        MapNerdController.CONTINENTS.forEach(code => {
            // Skip continents with no playable countries in the easy set.
            if (this._scopeCodes(code).length === 0) return;
            html += tile(code, lang.t(`mapExploration.continents.${code}`));
        });

        this.scopeGrid.innerHTML = html;
        this.scopeGrid.querySelectorAll(".scope-tile").forEach(btn => {
            btn.addEventListener("click", () => this.startScope(btn.dataset.scope));
        });
    }

    startScope(scope) {
        this.currentScope = scope;

        // Restore pristine markup (fresh SVG/hearts, no stale listeners/state).
        const fresh = this.pristineContainer.cloneNode(true);
        this.gameContainer.replaceWith(fresh);
        this.gameContainer = fresh;
        this.gameContainer.style.display = "";

        const freshProgress = this.pristineProgress.cloneNode(true);
        this.progressContainer.replaceWith(freshProgress);
        this.progressContainer = freshProgress;
        this.progressContainer.style.display = "";

        if (this.scopePicker) this.scopePicker.style.display = "none";
        // Back to the full-screen fixed layout for the map game.
        this.page.classList.remove("scope-selecting");

        const continent = scope === "world" ? null : scope;
        this.game = new this.GameClass(continent);

        this.maybeShowHelp();
        this.zoomToScope(continent);
    }

    goBack() {
        if (this.game && this.game.teardown) this.game.teardown();
        this.game = null;
        // Re-render so discovery percentages reflect the just-finished session.
        this.showPicker();
    }

    /**
     * Restart the current scope from scratch, without going through the scope
     * picker — clears the active game's persisted run (same keys as
     * `BaseGame.reloadButton()`) then re-instantiates it via `startScope()`.
     */
    restartScope() {
        if (!this.game) return;
        const gameId = this.game.gameId;
        ["countriesleft", "current", "proposals", "lifes", "finished", "startedAt", "errors"].forEach(key => {
            StorageService.remove(`${gameId}.${key}`);
        });
        if (this.game.teardown) this.game.teardown();
        this.startScope(this.currentScope);
    }

    /** Show the how-to-play popup once per visit, gated on the world preference key. */
    maybeShowHelp() {
        if (this.helpShown) return;
        this.helpShown = true;

        // Language-agnostic key so FR/EN share the same preference.
        const hideHelpKey = `geonerd.${this.worldGameId}.hideHelp`;
        // One-shot migration from the old language-prefixed key.
        if (!localStorage.getItem(hideHelpKey)) {
            if (localStorage.getItem(`fr.${this.worldGameId}.hideHelp`) === 'true' ||
                localStorage.getItem(`en.${this.worldGameId}.hideHelp`) === 'true') {
                localStorage.setItem(hideHelpKey, 'true');
            }
        }

        const hideHelp = localStorage.getItem(hideHelpKey) === 'true';
        const popupOverlay = this.page.querySelector(".help-popup-overlay");
        if (hideHelp || !popupOverlay) return;

        const description = popupOverlay.querySelector(".help-description");
        const helpKey = this.mode === "hard" ? "game.mapHardHelp" : "game.mapClassicHelp";
        description.innerHTML = geoNerdApp.languageManager.t(helpKey);
        popupOverlay.classList.add("show");

        const closeBtn = popupOverlay.querySelector(".btn-close-help");
        const checkbox = popupOverlay.querySelector("#dont-show-help");
        closeBtn.addEventListener("click", () => {
            if (checkbox && checkbox.checked) localStorage.setItem(hideHelpKey, 'true');
            popupOverlay.classList.remove("show");
        }, { once: true });
    }

    /**
     * Frame the chosen continent by zooming to the union of its country paths.
     * World scope keeps the default full-map view.
     */
    zoomToScope(continent) {
        if (!continent || !this.game || !this.game.mapInteraction) return;
        const codes = this._scopeCodes(continent);
        const map = this.gameContainer.querySelector(".map-container svg");
        if (!map) return;

        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        let any = false;
        codes.forEach(code => {
            const path = map.querySelector(`#${code}`);
            if (!path || typeof path.getBBox !== "function") return;
            const b = path.getBBox();
            if (b.width === 0 && b.height === 0) return;
            any = true;
            minX = Math.min(minX, b.x);
            minY = Math.min(minY, b.y);
            maxX = Math.max(maxX, b.x + b.width);
            maxY = Math.max(maxY, b.y + b.height);
        });
        if (!any) return;

        this.game.mapInteraction.zoomToBBox({
            x: minX,
            y: minY,
            width: maxX - minX,
            height: maxY - minY
        });
    }
}
